/**
 * ImpactSection.tsx - Impact Statistics Display Component
 *
 * This component displays impact statistics in a responsive grid layout with
 * icons, numbers, and descriptions. Adapts to different zone widths and supports
 * custom theming.
 *
 * Key Features:
 * - Responsive grid layout (4x1, 2x2, or 1x4 based on zone)
 * - Custom icon support with Lucide icons
 * - Color-coded impact items with hover effects
 * - Empty state handling with helpful messaging
 * - Zone-aware layout adaptation
 * - Live builder data integration
 *
 * Props:
 * - forceStacked?: boolean - Force single column layout
 * - zoneType?: string - Zone type for layout decisions
 *
 * Dependencies: MobX store, Lucide icons, styled-components
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { ImpactModel } from "../models/FundraisersGridModels";

const ImpactContainer = styled.div`
  padding: 0;
  margin: 0;
  text-align: center;
`;

const ImpactGrid = styled.div<{ zoneType?: string }>`
  display: grid;
  grid-template-columns: ${(props) => {
    if (props.zoneType === "left-column" || props.zoneType === "right-column") {
      return "repeat(2, 1fr)";
    }
    return "repeat(4, 1fr)";
  }};
  gap: 32px;
  width: 100%;
  max-width: ${(props) =>
    props.zoneType === "full-width" ? "none" : "1000px"};
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 20px;
    max-width: 100%;
    padding: 0 4px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const ImpactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 24px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  min-height: 200px;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 24px 16px;
    min-height: 180px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 20px 12px;
    min-height: 160px;
    gap: 8px;
  }
`;

const ImpactIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.color};
  margin-bottom: 8px;
  box-shadow: 0 4px 16px ${(props) => props.color}30;

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    margin-bottom: 6px;
  }

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    margin-bottom: 4px;
  }
`;

const ImpactNumber = styled.div<{ color: string }>`
  font-size: 32px;
  font-weight: 800;
  color: ${(props) => props.color};
  margin-bottom: 8px;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 6px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 4px;
  }
`;

const ImpactLabel = styled.div<{ color: string }>`
  font-size: 16px;
  color: ${(props) => props.color};
  font-weight: 600;
  text-align: center;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
`;

const EmptyTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

// Function to lighten a hex color
const lightenColor = (hex: string, percent: number) => {
  if (!hex || !hex.startsWith("#")) return hex;

  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

interface ImpactSectionProps {
  forceStacked?: boolean;
  zoneType?: "full-width" | "left-column" | "right-column";
  impacts: ImpactModel[];
  isPreviewMode?: boolean;
}

export const ImpactSection: React.FC<ImpactSectionProps> = observer(
  ({ forceStacked = false, zoneType, impacts, isPreviewMode }) => {
    // Use live builder data when available, otherwise fall back to saved fundraiser data
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;
    const hasLiveBuilderData = fundraiserStore.selectedFundraiserId === null;

    const renderIcon = (iconName: string, color: string) => {
      const Icon = (Icons as any)[iconName];
      return Icon ? <Icon size={28} /> : <Icons.Heart size={28} />;
    };

    //If no impacts are defined, don't render anything
    if (impacts.length === 0 && isPreviewMode) {
      return (
        <ImpactContainer>
          <EmptyState>
            <EmptyTitle>No Impact Items</EmptyTitle>
            <EmptyText>
              Add impact items in the Fundraiser Details step to showcase your
              mission's impact.
            </EmptyText>
          </EmptyState>
        </ImpactContainer>
      );
    }

    if (impacts.length === 0) {
      return null;
    }
    return (
      <ImpactContainer>
        <ImpactGrid zoneType={zoneType}>
          {impacts.map((item, index) => (
            <ImpactItem key={item.id || index}>
              <ImpactIcon color={item.color.hex}>
                {renderIcon(item.icon, item.color.hex)}
              </ImpactIcon>
              <ImpactNumber color={item.color.hex}>{item.number}</ImpactNumber>
              <ImpactLabel color={item.color.hex}>
                {item.description}
              </ImpactLabel>
            </ImpactItem>
          ))}
        </ImpactGrid>
      </ImpactContainer>
    );
  }
);
